import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import { goBack } from "../../utils/navigation";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TaskDetailPanel from "../../components/task/TaskDetailPanel";
import EntityForm from "../../components/design/EntityForm";
import { PageLoading } from "../../components/loading";
import { useApp } from "../../design/AppContext";
import { apiGet, apiPutForm } from "../../utils/api";
import { apiErrorMessage } from "../../utils/apiError";
import { jwtEncode } from "../../utils/jwt";
import {
  FORM_EDIT_TITLES,
  formConfig,
} from "../../resources/forms";
import {
  LABEL_TO_PRIO,
  LABEL_TO_STATUS,
  PRIO_CODE,
  PRIO_LABEL,
  STATUS_CODE,
  STATUS_LABEL,
  fmtInputDate,
  memberRoleLabel,
  prioKey,
  statusKey,
} from "../../design/domain";

/**
 * 工項詳情整頁 /Task/:id（設計稿 #screen-task，el-pm-template_4.html:625-757）
 *
 * 這一頁自己不畫任何版面：內容三段（一眼區／工作區／參考區）全部在
 * components/task/TaskDetailPanel 裡，抽屜（/Board、/MyTask）用的是同一支元件的
 * embedded 模式，兩邊看到的東西才會一樣。
 *
 * 外層不另外包頁面容器：Layout.js 的內容區本身就有內距與寬度上限。
 *
 * ── 為什麼編輯表單在這裡 ──
 * 工項詳情以前是專案詳情的頁內面板，編輯表單也掛在那一頁。專案詳情改版後
 * ?task= 一律導到這裡（見 pages/Project/Detail.js 檔頭），那張編輯表單就沒有家了 ——
 * 標題、誰負責、什麼時候交、里程碑這些欄位會變成整個系統都改不到。
 * 功能不准刪，所以把它接在這一頁：詳情裡的「⋯ 更多 → 修改這件事」切成表單，
 * 存檔後切回詳情並重新載入。
 */

/** /Task 回的欄位名是 snake_case；取值統一走 lodash，避免 undefined 炸掉 */
function TaskEditForm({ taskId, onCancel, onSaved }) {
  const { toast } = useApp();
  const [raw, setRaw] = useState(null);
  const [members, setMembers] = useState([]);
  const [phases, setPhases] = useState([]);
  const [values, setValues] = useState({});
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  /* 載入這一件工項本身 —— 表單的初值要用「現在後端真正存的值」，不是清單頁手上的快照 */
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const t = await apiGet("/Task", {
          Data: jwtEncode({ TaskId: Number(taskId) }),
          id: taskId,
          taskId,
        });
        if (alive) setRaw(t || null);
      } catch (e) {
        if (alive) setRaw(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [taskId]);

  /* 成員與里程碑是兩個下拉的選項來源；取不到就讓那一欄空著，不要讓整張表單開不起來 */
  const projectId = Number(_.get(raw, "project_id") || 0);
  useEffect(() => {
    if (!projectId) return undefined;
    let alive = true;
    apiGet("/Project/Member/List", { Data: jwtEncode({ ProjectId: projectId }), projectId })
      .then((res) => {
        if (!alive) return;
        const arr = Array.isArray(res) ? res : _.get(res, "data_list", []) || [];
        setMembers(
          arr
            .map((m) => {
              const account = m.account || m.Account || "";
              const name = m.name || m.Name || account;
              const role = memberRoleLabel(m.role ?? m.Role);
              // 選項文字帶角色：同名同姓分得出來（與 /Task/New 同一套寫法）
              return { account, name, label: role ? `${name}（${role}）` : name };
            })
            .filter((m) => m.account)
        );
      })
      .catch(() => {});
    apiGet(`/Phase/List/${projectId}`)
      .then((res) => {
        if (!alive) return;
        const arr = Array.isArray(res) ? res : _.get(res, "data_list", []) || [];
        setPhases(
          arr.map((p) => ({ id: p.id ?? p.Id, name: p.name || p.Name || "" })).filter((p) => p.name)
        );
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [projectId]);

  /* 後端的值 → 表單看得懂的中文標籤。成員清單晚一步回來，所以要等它到齊再填「誰負責」 */
  useEffect(() => {
    if (!raw) return;
    const acc = _.get(raw, "assignee") || "";
    const hit = members.find((m) => m.account === acc);
    setValues({
      title: _.get(raw, "title") || "",
      assignee: hit ? hit.label : "",
      due_date: _.get(raw, "due_date") ? fmtInputDate(_.get(raw, "due_date")) : "",
      issue_key: _.get(raw, "issue_key") || "",
      phase: _.get(raw, "phase_name") || "",
      status: STATUS_LABEL[statusKey(_.get(raw, "status"))] || "",
      priority: PRIO_LABEL[prioKey(_.get(raw, "priority"))] || "",
      start_date: _.get(raw, "start_date") ? fmtInputDate(_.get(raw, "start_date")) : "",
      estimated_hours:
        _.get(raw, "estimated_hours") == null ? "" : String(_.get(raw, "estimated_hours")),
      description: _.get(raw, "description") || "",
    });
  }, [raw, members]);

  const fields = useMemo(() => {
    const base = formConfig(
      "items",
      members.map((m) => m.label),
      phases.map((p) => p.name)
    );
    /*
     * 「這是哪一種」在編輯時拿掉：PUT /Task 的 ReqPutTask 根本沒有型別欄位
     * （GET /Task 也沒有回），留著會是一個改了不會生效、而且填不出現值的下拉。
     * 型別要改請重開一件 —— 這比給一個假的選單誠實。
     */
    /*
     * 「誰負責」在新增時是必填（沒人認領的工項等於沒建），但編輯時不能跟著必填：
     * 系統裡早就有一批沒有指派人的舊工項，跟著必填的話，那些工項連改個標題
     * 都會被擋住，而且錯誤訊息講的是一件使用者本來沒打算改的事。
     * 這裡只放寬「擋不擋得住送出」，欄位本身照樣在、照樣選得到。
     */
    /*
     * 狀態下拉拿掉「已完成」（本來就是已完成的除外，不然表單一打開就對不上現值）。
     * 已完成只能經審核通過自動完成，或走工作流的轉換（守門會檢查審核）；
     * PUT /Task 不跑那道守門，留著這個選項等於一條不用審核就能完成的後門。
     */
    const isDone = statusKey(_.get(raw, "status")) === "done";
    return base
      .filter((f) => f.key !== "type")
      .map((f) => (f.key === "assignee" ? { ...f, req: false, rec: true } : f))
      .map((f) =>
        f.key === "phase" && phases.length === 0
          ? { ...f, hint: "此專案尚未建立里程碑，可至「時程 → 新增里程碑」建立後再選擇；也可先留空。" }
          : f
      )
      .map((f) =>
        f.key === "status" && !isDone && Array.isArray(f.options)
          ? { ...f, options: f.options.filter((o) => o !== STATUS_LABEL.done) }
          : f
      );
  }, [members, phases, raw]);

  const handleChange = (key, val) => setValues((prev) => ({ ...prev, [key]: val }));

  const submit = async () => {
    if (sending || !raw) return;
    const title = String(values.title || "").trim();
    if (!title) {
      toast("請填寫工項標題", "error");
      return;
    }
    const member = members.find((m) => m.label === values.assignee);
    const phase = phases.find((p) => p.name === values.phase);
    /*
     * PUT /Task 是整筆覆蓋：沒帶的欄位會被清成 null。
     * 表單上沒有的欄位（實際工時、進度、標籤、父工項）一定要帶回原值，
     * 否則「改一個截止日」會順手把進度歸零。
     */
    const payload = {
      TaskId: Number(raw.id),
      PhaseId: phase ? Number(phase.id) : null,
      ParentTaskId: raw.parent_task_id ?? null,
      Title: title,
      Description: values.description || "",
      Status: STATUS_CODE[LABEL_TO_STATUS[values.status]] ?? (Number(raw.status) || 0),
      Priority: PRIO_CODE[LABEL_TO_PRIO[values.priority]] ?? (Number(raw.priority) || 0),
      Assignee: member ? member.account : null,
      StartDate: values.start_date || null,
      DueDate: values.due_date || null,
      EstimatedHours: values.estimated_hours === "" ? null : Number(values.estimated_hours),
      ActualHours: raw.actual_hours ?? null,
      Progress: Number(raw.progress) || 0,
      Tags: raw.tags || null,
    };

    setSending(true);
    try {
      await apiPutForm("/Task", { Data: jwtEncode(payload), ...payload });
      toast("已儲存變更");
      onSaved();
    } catch (e) {
      toast(apiErrorMessage(e, "儲存失敗"), "error");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <PageLoading />;
  }

  if (!raw) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h2">無法開啟此工項</Typography>
        <Typography>此工項可能已被刪除，或您沒有檢視權限。</Typography>
      </Box>
    );
  }

  return (
    <>
      <EntityForm
        title={FORM_EDIT_TITLES.items}
        subtitle={
          _.get(raw, "project_name") ? `所屬專案：${_.get(raw, "project_name")}` : ""
        }
        fields={fields}
        values={values}
        onChange={handleChange}
        onBack={onCancel}
        onSubmit={submit}
        submitText="儲存並返回"
        busy={sending}
      />
      {/* max-width 對齊 EntityForm 卡片的 640px，否則這句話會橫跨整個內容寬、跟卡片對不齊。 */}
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640, mt: 2 }}>
        代碼與類型於建立時決定，無法修改。進度與回報請至工項頁的「我的回報」。
      </Typography>
    </>
  );
}

TaskEditForm.propTypes = {
  taskId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onCancel: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

function TaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  /* 存檔後把詳情整個重掛，讓它重新跟後端要一次資料（改過的標題、日期才會換掉） */
  const [version, setVersion] = useState(0);

  /* 換到另一件工項時，編輯表單不能還開著 —— 那會變成用 A 的表單改 B */
  useEffect(() => {
    setEditing(false);
  }, [id]);

  const openTask = useCallback(
    (d) => {
      const next = _.get(d, "id");
      if (next != null) navigate(`/Task/${next}`);
    },
    [navigate]
  );

  if (editing) {
    return (
      <TaskEditForm
        taskId={id}
        onCancel={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          setVersion((v) => v + 1);
        }}
      />
    );
  }

  return (
    <TaskDetailPanel
      key={`task-${id}-${version}`}
      taskId={id}
      /* 有上一頁就回上一頁；直接開網址進來的（沒有上一頁）回個人工項，不要退出網站 */
      onBack={() => goBack(navigate, null, "/MyTask")}
      onEdit={() => setEditing(true)}
      onOpenTask={openTask}
    />
  );
}

export default TaskDetail;
